import { useState, useMemo, useEffect } from 'react';
import {
  Container,
  Heading,
  Text,
  Button,
  Flex,
  Box,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Select,
  VStack,
  useDisclosure,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { TrendingUp, Sparkles } from 'lucide-react';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from '../../../contexts/AuthContext';
import { useWinsExcited } from './useWinsExcited';
import { WinsExcitedStats } from './components/WinsExcitedStats';
import { WinsExcitedCard } from './components/WinsExcitedCard';
import { WinExcitedModal } from '../Rocks/components/WinExcitedModal';
import { ConfirmModal } from '../Rocks/components/ConfirmModal';
import { WinExcited, User } from '../types';

/**
 * Helper function to convert Timestamp or Date to Date
 */
function toDate(value: Timestamp | Date | string): Date {
  if (value instanceof Date) {
    return value;
  }
  if (value && typeof value === 'object' && 'toDate' in value) {
    return (value as Timestamp).toDate();
  }
  return new Date(value as string);
}

/**
 * Wins & Excited Page
 * Main page for viewing and managing wins and excited items
 * Migrated from Supabase/Tailwind to Firestore/Chakra UI
 */
export function WinsExcitedPage() {
  const { currentUser } = useAuth();
  const {
    winsExcited,
    users,
    loading,
    addWinExcited,
    deleteWinExcited,
  } = useWinsExcited(currentUser?.uid);

  const [typeFilter, setTypeFilter] = useState<'all' | 'win' | 'excited'>('all');
  const [userFilter, setUserFilter] = useState<string>('all');
  const [timeFilter, setTimeFilter] = useState<string>('all');
  const [selectedWinId, setSelectedWinId] = useState<string | null>(null);

  const { isOpen: isAddOpen, onOpen: onAddOpen, onClose: onAddClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const toast = useToast();

  // Join entries with user data
  const joinedEntries = useMemo(() => {
    const usersMap = new Map(users.map((u) => [u.id, u]));
    return winsExcited.map((entry) => ({
      ...entry,
      user: usersMap.get(entry.userId),
    })) as (WinExcited & { user?: User })[];
  }, [winsExcited, users]);

  // Filter entries
  const filteredEntries = useMemo(() => {
    let filtered = joinedEntries;

    // Filter by type
    if (typeFilter !== 'all') {
      filtered = filtered.filter((entry) => entry.type === typeFilter);
    }

    // Filter by user
    if (userFilter !== 'all') {
      filtered = filtered.filter((entry) => entry.userId === userFilter);
    }

    // Filter by time
    if (timeFilter !== 'all') {
      const now = new Date();
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay()); // Sunday
      startOfWeek.setHours(0, 0, 0, 0);

      const startOfLastWeek = new Date(startOfWeek);
      startOfLastWeek.setDate(startOfWeek.getDate() - 7);

      filtered = filtered.filter((entry) => {
        const entryDate = toDate(entry.createdAt);
        if (timeFilter === 'this-week') {
          return entryDate >= startOfWeek;
        } else if (timeFilter === 'last-week') {
          return entryDate >= startOfLastWeek && entryDate < startOfWeek;
        }
        return true;
      });
    }

    return filtered;
  }, [joinedEntries, typeFilter, userFilter, timeFilter]);

  // Calculate stats from filtered entries
  const stats = useMemo(() => {
    return {
      total: filteredEntries.length,
      wins: filteredEntries.filter((e) => e.type === 'win').length,
      excited: filteredEntries.filter((e) => e.type === 'excited').length,
    };
  }, [filteredEntries]);

  const handleAddWinExcited = async (
    type: 'win' | 'excited',
    content: string,
    taggedUserId: string | null,
    currentUserId: string
  ) => {
    try {
      await addWinExcited(type, content, taggedUserId, currentUserId);
      toast({
        title: 'Success',
        description: `${type === 'win' ? 'Win' : 'Excitement'} shared!`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      onAddClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDeleteWinExcited = async () => {
    if (!selectedWinId) return;
    try {
      await deleteWinExcited(selectedWinId);
      toast({
        title: 'Success',
        description: 'Entry deleted successfully',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      onDeleteClose();
      setSelectedWinId(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete entry',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Color mode values for dark mode support
  const headingColor = useColorModeValue('gray.900', 'white');
  const subheadingColor = useColorModeValue('gray.600', 'gray.400');
  const selectBg = useColorModeValue('white', 'gray.700');
  const emptyTextColor = useColorModeValue('gray.500', 'gray.400');

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Text color="gray.500">Loading wins & excited...</Text>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={4}>
        <Box>
          <Heading size="lg" color={headingColor}>
            Wins & Excited
          </Heading>
          <Text color={subheadingColor} mt={1}>
            Share victories and what you're excited about
          </Text>
        </Box>
        <Button
          leftIcon={<AddIcon />}
          colorScheme="blue"
          onClick={onAddOpen}
        >
          Add Win / Excited
        </Button>
      </Flex>

      {/* Stats Cards */}
      <Box mb={6}>
        <WinsExcitedStats total={stats.total} wins={stats.wins} excited={stats.excited} />
      </Box>

      {/* Filter Bar */}
      <Box mb={6}>
        <Flex wrap="wrap" gap={4} align="center">
          <Tabs
            index={typeFilter === 'all' ? 0 : typeFilter === 'win' ? 1 : 2}
            onChange={(index) => {
              const types: ('all' | 'win' | 'excited')[] = ['all', 'win', 'excited'];
              setTypeFilter(types[index]);
            }}
          >
            <TabList>
              <Tab>All ({stats.total})</Tab>
              <Tab>Wins ({stats.wins})</Tab>
              <Tab>Excited ({stats.excited})</Tab>
            </TabList>
          </Tabs>

          <Select
            placeholder="All Users"
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            width="200px"
            bg={selectBg}
          >
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name}
              </option>
            ))}
          </Select>

          <Select
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value)}
            width="150px"
            bg={selectBg}
          >
            <option value="all">All Time</option>
            <option value="this-week">This Week</option>
            <option value="last-week">Last Week</option>
          </Select>
        </Flex>
      </Box>

      {/* List */}
      <VStack spacing={4} align="stretch" mt={4}>
        {filteredEntries.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Text color={emptyTextColor}>No entries found. Add your first win or excited item!</Text>
          </Box>
        ) : (
          filteredEntries.map((entry) => (
            <WinsExcitedCard
              key={entry.id}
              entry={entry}
              currentUserId={currentUser?.uid || ''}
              onDelete={() => {
                setSelectedWinId(entry.id);
                onDeleteOpen();
              }}
            />
          ))
        )}
      </VStack>

      {/* Modals */}
      <WinExcitedModal
        isOpen={isAddOpen}
        onClose={onAddClose}
        currentUserId={currentUser?.uid}
        users={users}
        onSave={handleAddWinExcited}
      />

      <ConfirmModal
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        title="Delete Entry"
        message="Are you sure you want to delete this entry? This action cannot be undone."
        confirmText="Delete"
        confirmColorScheme="red"
        onConfirm={handleDeleteWinExcited}
      />
    </Container>
  );
}

